<template>
	<div>
		<slot name="label">
			<div v-if="label" class="tw-pb-1.5 tw-font-medium tw-text-gray-600">
				<span>{{ label }}</span>
				<span v-if="props.rules.includes('required')" class="tw-text-error-400">*</span>
			</div>
		</slot>
		<div>
			<AppMenu
				placement="bottom"
				:closeWhenClickInside="!multiple"
				@open="onMenuOpened"
				@close="isMenuOpened = false"
			>
				<template #trigger>
					<div
						class="tw-flex tw-items-center"
						@click="updateSelectWidth($event)"
						ref="containerRef"
					>
						<div
							class="tw-flex tw-max-w-full tw-flex-1 tw-items-center tw-justify-between tw-gap-2 tw-rounded-lg tw-border tw-transition-all"
							:class="computedClasses"
							tabindex="0"
						>
							<slot name="start-icon">
								<InlineSvg
									:src="loadFile('assets/v2/image/icons/search.svg')"
									class="tw-min-w-5 tw-text-gray-500"
								/>
							</slot>
							<div
								class="tw-flex tw-min-w-0 tw-flex-1 tw-items-center"
								style="min-height: 24px"
							>
								<div v-if="placeholder && showPlaceholder" class="tw-text-gray-500">
									{{ placeholder }}
								</div>
								<template v-else>
									<slot name="selected-options" v-bind="{ selectedOptions }">
										<div
											class="tw-min-w-0 tw-overflow-hidden tw-text-ellipsis tw-whitespace-nowrap"
										>
											{{
												selectedOptions
													.map((option) => getOptionLabel(option))
													.join(', ')
											}}
										</div>
									</slot>
								</template>
							</div>
							<InlineSvg
								v-if="clearable && selectedOptions.length > 0"
								:src="loadFile('assets/v2/image/icons/close.svg')"
								class="tw-text-gray-500"
								@click.stop="clearSelectedOptions"
							/>
							<InlineSvg
								v-else
								:src="loadFile('assets/v2/image/icons/arrow-down.svg')"
								class="tw-text-gray-500 tw-transition"
								:class="{ 'tw-rotate-180': isMenuOpened }"
							/>
						</div>
					</div>
				</template>
				<div
					class="tw-flex tw-flex-col tw-overflow-y-auto tw-rounded-lg tw-border tw-border-gray-200 tw-p-2"
					style="max-height: 320px"
					:style="{ width: selectWidth }"
				>
					<AppInput
						v-if="
							(fetchDataFunction && !hideSearchField) ||
							searchFilterFunction ||
							searchWithFirebaseStore
						"
						v-model="search"
						class="tw-w-full tw-pb-2"
						:size="size"
						:debounce="searchDebounce"
						ref="searchInput"
					/>
					<component
						:is="fetchDataFunction || firebaseStoreData ? PaginatedScrollArea : 'div'"
						:fetchDataFunction="fetchDataFunction"
						:firebaseStoreData="firebaseStoreData"
						:filters="filters"
						@input="setOptions"
						style="max-height: 320px; min-height: 50px"
						:spinnerAttrs="{ size: '30px' }"
						:fetchOnMounted="false"
						emitData
						:limit="10"
						ref="paginatedScrollAreaRef"
					>
						<template v-slot="scrollAreaData">
							<div v-if="!filteredOptions.length && !scrollAreaData?.loading">
								<slot name="no-option">
									<div class="tw-py-4 tw-text-center tw-capitalize tw-text-gray-500">
										{{ $t('no-options-found') }}
									</div>
								</slot>
							</div>
							<div v-for="(option, i) in filteredOptions" :key="i">
								<slot
									name="option"
									v-bind="{
										option,
										isSelectedOption,
										selected: isSelectedOption(option),
										selectOption,
									}"
								>
									<div
										class="tw-flex tw-flex-1 tw-items-center tw-justify-between tw-rounded-md tw-p-2.5 hover:tw-bg-gray-100"
										:class="{
											'tw-bg-gray-50': isSelectedOption(option),
											'tw-cursor-not-allowed': isDisabledOption(option),
											'tw-cursor-pointer': !isDisabledOption(option),
										}"
										@click="selectOption(option)"
									>
										<div class="tw-truncate tw-text-gray-900">
											{{ getOptionLabel(option) }}
										</div>
										<InlineSvg
											v-if="isSelectedOption(option)"
											:src="loadFile('assets/v2/image/icons/check.svg')"
											class="tw-shrink-0 tw-text-brand-600"
										/>
									</div>
								</slot>
							</div>
						</template>
					</component>
				</div>
			</AppMenu>
		</div>

		<div
			:class="rules.length || hint || allocateBottomSpace ? 'tw-pt-1 tw-text-sm' : ''"
			:style="rules.length || hint || allocateBottomSpace ? 'min-height: 20px' : ''"
		>
			<div v-if="!errorMessage" class="tw-text-gray-600">
				{{ hint }}
			</div>
			<div v-if="errorMessage" class="tw-text-error-500">
				{{ errorMessage }}
			</div>
		</div>
	</div>
</template>

<script setup>
import AppInput from './input.vue';
import AppMenu from '../app-menu.vue';
import InlineSvg from '../inline-svg';
import PaginatedScrollArea from '../paginated-scroll-area.vue';

import { computed, onMounted, ref, nextTick } from 'vue';
import { debounce as debounceFunc } from 'lodash';
import { useFieldValidation } from '@/v2/utils/forms';
import { loadFile } from '@/v2/utils/index';

const props = defineProps({
	modelValue: {},
	options: {
		type: [Array, Function],
		default: () => [],
	},
	label: {
		type: String,
		default: '',
	},
	placeholder: {
		type: String,
		default: '',
	},
	hint: {
		type: String,
		default: '',
	},
	size: {
		type: String,
		default: 'md',
	},
	debounce: {
		type: Number,
		default: 0,
	},
	searchDebounce: {
		type: Number,
		default: 700,
	},
	multiple: {
		type: Boolean,
		default: false,
	},
	disabled: {
		type: Boolean,
		default: false,
	},
	clearable: {
		type: Boolean,
		default: undefined,
	},
	returnObject: {
		type: Boolean,
		default: false,
	},
	rules: {
		type: Array,
		default: () => [],
	},
	optionLabel: {
		type: [String, Function],
		default: 'label',
	},
	optionValue: {
		type: [String, Function],
		default: 'value',
	},
	fetchDataFunction: {
		type: [Function],
		required: false,
	},
	searchFilterKey: {
		type: String,
		default: 'name',
	},
	allocateBottomSpace: {
		type: Boolean,
		default: false,
	},
	disableOptionFunction: {
		type: Function,
		default: undefined,
	},
	hideSearchField: {
		type: Boolean,
		default: false,
	},
	firebaseStoreData: {
		type: Object,
	},
	searchFilterFunction: {
		type: Function,
	},
	searchWithFirebaseStore: {
		type: Boolean,
		default: false,
	},
});

const emit = defineEmits(['update:modelValue']);

const search = ref('');
const containerRef = ref(null);
const isMenuOpened = ref(false);
const internalOptions = ref([]);
const paginatedScrollAreaRef = ref(null);
const isPaginatedScrollAreaInit = ref(false);
const searchInput = ref(null);

const emitModel = (val) => {
	if ((isClearable.value || props.multiple) && isSelectedOption(val)) {
		if (props.multiple) {
			emit(
				'update:modelValue',
				props.modelValue.filter((opt) => !isMatchOption(opt, val)),
			);
		} else {
			emit('update:modelValue', undefined);
		}
	} else if (!isSelectedOption(val)) {
		if (!props.multiple) {
			if (props.returnObject) {
				emit('update:modelValue', val);
				return;
			}
			emit('update:modelValue', getOptionValue(val));
		} else {
			if (props.returnObject) {
				emit('update:modelValue', [...props.modelValue, val]);
				return;
			}
			emit('update:modelValue', [...props.modelValue, getOptionValue(val)]);
		}
	}
};

const inputModel = computed({
	get() {
		if (props.multiple) {
			return props.modelValue || [];
		}
		return props.modelValue;
	},
	set: props.debounce ? debounceFunc(emitModel, props.debounce) : emitModel,
});

const computedOptions = computed(() => {
	if (props.fetchDataFunction || props.firebaseStoreData) {
		return internalOptions.value;
	}
	return props.options;
});

const filteredOptions = computed(() => {
	if (props.searchFilterFunction) {
		return props.searchFilterFunction({
			options: computedOptions.value,
			search: search.value,
		});
	}
	return computedOptions.value;
});

const filters = computed(() => {
	return {
		[props.searchFilterKey]: search.value || undefined,
	};
});

const { errorMessage, hasErrorMessage } = useFieldValidation(
	() => props.modelValue,
	() => props.rules,
);

const showPlaceholder = computed(() => {
	if (props.multiple) {
		return inputModel.value.length === 0;
	}
	return !inputModel.value;
});

const selectedOptions = computed(() => {
	if (props.multiple) {
		const res = computedOptions.value.filter((opt) =>
			inputModel.value.some((selectedOption) => isMatchOption(selectedOption, opt)),
		);
		if (res.length === inputModel.value.length) return res;
		return inputModel.value;
	}

	const res = computedOptions.value.filter((opt) => isMatchOption(inputModel.value, opt));
	if (res.length !== 0) return res;
	if (inputModel.value === undefined || inputModel.value === null) return [];
	return [getOptionLabel(inputModel.value)];
});

const isClearable = computed(() => {
	if (props.clearable !== undefined) return props.clearable;
	const isRequired = props.rules.some((rule) => rule === 'required');
	return !isRequired;
});

const isMatchOption = (option1, option2) => {
	return getOptionValue(option1) === getOptionValue(option2);
};
const isSelectedOption = (option) => {
	if (!props.multiple) {
		return isMatchOption(inputModel.value, option);
	}
	return inputModel.value.some((selectedOption) => isMatchOption(selectedOption, option));
};

const isDisabledOption = (option) => {
	if (!props.disableOptionFunction) return false;
	return props.disableOptionFunction(option);
};

const selectOption = (option) => {
	if (isDisabledOption(option)) return;
	inputModel.value = option;
	search.value = '';
};

const getOptionLabel = (option) => {
	if (!option) return '';
	if (typeof option !== 'object') return option;
	if (typeof props.optionLabel === 'string') {
		return option[props.optionLabel];
	}
	if (typeof props.optionLabel !== 'function') {
		console.warn('[AppSelect] App Select optionLabel Must be String | Function');
		return;
	}
	return props.optionLabel(option);
};

const getOptionValue = (option) => {
	if (option === undefined || option === null) return;
	if (typeof option !== 'object') return option;
	if (typeof props.optionValue === 'string') {
		return option[props.optionValue];
	}
	if (typeof props.optionValue !== 'function') {
		console.warn('[AppSelect] App Select optionValue Must be String | Function');
		return;
	}
	return props.optionValue(option);
};

const setOptions = (data) => {
	internalOptions.value = data;
	nextTick(() => {
		KTMenu.updateDropdowns();
	});
};

const onMenuOpened = () => {
	isMenuOpened.value = true;
	searchInput.value?.focus?.();
	if ((props.fetchDataFunction || props.firebaseStoreData) && !isPaginatedScrollAreaInit.value) {
		paginatedScrollAreaRef.value?.init?.();
		isPaginatedScrollAreaInit.value = true;
	}
};

const clearSelectedOptions = () => {
	if (props.multiple) {
		emit('update:modelValue', []);
	} else {
		emit('update:modelValue', null);
	}
};

const selectWidth = ref('0px');
const updateSelectWidth = (evt) => {
	nextTick(() => {
		if (props.disabled) evt?.stopPropagation();
		selectWidth.value = (containerRef.value?.clientWidth || 0) + 'px';
	});
};
onMounted(() => {
	updateSelectWidth();
});

const sizeClasses = {
	xs: 'tw-px-2 tw-py-0.5',
	sm: 'tw-px-3 tw-py-2',
	md: 'tw-px-3.5 tw-py-2.5',
};
const computedClasses = computed(() => {
	const res = [];
	if (sizeClasses[props.size]) {
		res.push(sizeClasses[props.size]);
	} else {
		console.warn('[AppSelect] You are using unsupported size in app-input component', props.size);
	}
	if (hasErrorMessage.value) {
		res.push(
			'tw-border-error-300 focus:tw-border-error-300 focus:tw-ring focus:tw-ring-error-500/[.24]',
		);
	} else {
		res.push('tw-border-gray-300');
	}
	if (!hasErrorMessage.value && !props.disabled) {
		res.push('focus:tw-border-brand-300');
	}
	if (props.disabled) {
		res.push('tw-text-gray-500 tw-cursor-not-allowed');
	} else {
		res.push('tw-cursor-pointer');
	}
	return res;
});
</script>

<style lang="scss" scoped></style>

<style lang="scss" scoped>
@import 'tailwindcss/base';
@import 'tailwindcss/utilities';
</style>
